import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X } from "@phosphor-icons/react";
import confetti from "canvas-confetti";

const BRAND = ["#C026D3", "#FF1F8B", "#FF4422", "#FFC7DB", "#FFFFFF"];
const SEEN_KEY = "ml-award-seen";

export default function AwardPopup() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (sessionStorage.getItem(SEEN_KEY)) return;
    } catch (e) { /* storage unavailable, show anyway */ }
    const t = setTimeout(() => {
      setOpen(true);
      try { sessionStorage.setItem(SEEN_KEY, "1"); } catch (e) { /* ignore */ }
    }, 3000);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    if (!open) return;
    confetti({ particleCount: 130, spread: 85, origin: { y: 0.6 }, colors: BRAND, zIndex: 200 });
    const end = Date.now() + 1300;
    const frame = () => {
      confetti({ particleCount: 3, angle: 60, spread: 60, origin: { x: 0, y: 0.7 }, colors: BRAND, zIndex: 200 });
      confetti({ particleCount: 3, angle: 120, spread: 60, origin: { x: 1, y: 0.7 }, colors: BRAND, zIndex: 200 });
      if (Date.now() < end) requestAnimationFrame(frame);
    };
    frame();
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          onClick={() => setOpen(false)}
          data-testid="award-popup"
          role="dialog"
          aria-modal="true"
          aria-label="Award announcement"
          className="fixed inset-0 z-[90] flex items-center justify-center bg-black/60 backdrop-blur-sm px-4"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.9, y: 20 }}
            transition={{ type: "spring", damping: 22, stiffness: 300 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-md rounded-[32px] bg-white p-8 md:p-10 text-center shadow-2xl"
          >
            <div className="absolute -top-px left-12 right-12 h-1 brand-gradient rounded-full" />
            <button
              onClick={() => setOpen(false)}
              data-testid="award-popup-close"
              aria-label="Close"
              className="absolute top-4 right-4 inline-flex h-9 w-9 items-center justify-center rounded-full bg-zinc-100 text-zinc-500 hover:bg-zinc-200 transition-colors"
            >
              <X size={16} weight="bold" />
            </button>

            <img
              src="/images/prestige-award.webp"
              alt="NI Prestige Awards 2026/27 Winner"
              className="mx-auto h-28 md:h-32 w-auto"
            />

            <p className="overline text-zinc-500 mt-6 mb-3">Some news</p>
            <h2 className="font-display font-extrabold text-3xl md:text-4xl tracking-tighter text-zinc-900 leading-[0.98]">
              Digital Marketplace <span className="gradient-text">of the Year.</span>
            </h2>
            <p className="mt-4 text-sm md:text-base text-zinc-600 leading-relaxed">
              NI Prestige Awards 2026/27. Best in Northern Ireland.
            </p>
            <p className="mt-1 text-sm text-zinc-400">
              Not bad for a local outfit.
            </p>

            <button
              onClick={() => setOpen(false)}
              data-testid="award-popup-dismiss"
              className="mt-8 inline-flex items-center justify-center gap-2 px-7 py-3.5 rounded-full brand-gradient text-white font-semibold shadow-lg shadow-[#FF1F8B]/30 hover:-translate-y-0.5 transition-transform"
            >
              Back to the food
            </button>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
